import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  useTranslationExport,
  useBulkUpsertTranslations,
} from '@/features/i18n/api';
import type { TranslationExportRow } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/i18n/$tag')({
  component: I18nEditorPage,
});

/** Shape of an import file: [{ key, value }] or the export shape with currentValue. */
type ImportRow = { key: string; value?: string | null; currentValue?: string | null };

function I18nEditorPage() {
  const { tag } = Route.useParams();
  const { data, isLoading, isError, error } = useTranslationExport(tag);
  const upsert = useBulkUpsertTranslations(tag);

  // Local edits keyed by message key. A key present here overrides the
  // server-provided currentValue in the input; on save we send the diff.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const rows: TranslationExportRow[] = data?.rows ?? [];

  const valueFor = (row: TranslationExportRow): string =>
    edits[row.key] ?? row.currentValue ?? '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (missingOnly) {
        const hasValue = (edits[row.key] ?? row.currentValue ?? '').trim().length > 0;
        if (hasValue) return false;
      }
      if (q) {
        const hay = `${row.key} ${row.context} ${row.defaultMessage} ${valueFor(row)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, missingOnly, edits]);

  const dirtyEntries = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(edits)) {
      const original = rows.find((r) => r.key === key)?.currentValue ?? '';
      if (value !== original) out[key] = value;
    }
    return out;
  }, [edits, rows]);

  const dirtyCount = Object.keys(dirtyEntries).length;

  const handleSave = async () => {
    setSaveError(null);
    setSavedCount(null);
    if (dirtyCount === 0) return;
    try {
      const res = await upsert.mutateAsync({ messages: dirtyEntries });
      setSavedCount(res.written);
      setEdits({});
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save translations');
    }
  };

  const handleExport = () => {
    const payload = rows.map((r) => ({
      key: r.key,
      context: r.context,
      defaultMessage: r.defaultMessage,
      currentValue: valueFor(r) || null,
    }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `honeycomb-i18n-${tag}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (file: File) => {
    setImportError(null);
    setSavedCount(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setImportError('File is not valid JSON.');
      return;
    }
    if (!Array.isArray(parsed)) {
      setImportError('Expected a JSON array of { key, currentValue } rows.');
      return;
    }
    // Client-side validation against the registry key list before applying.
    const knownKeys = new Set(rows.map((r) => r.key));
    const next: Record<string, string> = {};
    const unknownKeys: string[] = [];
    for (const item of parsed as ImportRow[]) {
      if (!item || typeof item.key !== 'string') {
        setImportError('Every row must have a string `key`.');
        return;
      }
      if (!knownKeys.has(item.key)) {
        unknownKeys.push(item.key);
        continue;
      }
      const value = item.currentValue ?? item.value ?? '';
      next[item.key] = typeof value === 'string' ? value : '';
    }
    if (unknownKeys.length > 0) {
      setImportError(
        `${unknownKeys.length} key(s) not in the registry were skipped: ${unknownKeys
          .slice(0, 5)
          .join(', ')}${unknownKeys.length > 5 ? '…' : ''}`,
      );
    }
    setEdits((prev) => ({ ...prev, ...next }));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6" data-testid="i18n-editor-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/i18n" className="text-xs text-ink-faint hover:underline">
              ← Localization
            </Link>
          </div>
          <h1 className="font-serif text-3xl leading-tight">
            Translations · <span className="font-mono text-2xl">{tag}</span>
          </h1>
          <p className="text-ink-soft text-sm mt-1">
            {rows.length} registered key{rows.length === 1 ? '' : 's'}. Edit values inline and save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            data-testid="i18n-editor-import-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleImportFile(f);
              e.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            data-testid="i18n-editor-import"
            onClick={() => fileInputRef.current?.click()}
          >
            Import JSON
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="i18n-editor-export"
            onClick={handleExport}
          >
            Export JSON
          </Button>
        </div>
      </header>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            label="Search"
            placeholder="Filter by key or text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="i18n-editor-search"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-soft pb-2 select-none">
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
            data-testid="i18n-editor-missing-only"
          />
          Missing only
        </label>
        <div className="flex items-center gap-2 pb-1">
          <Button
            variant="primary"
            size="sm"
            data-testid="i18n-editor-save"
            disabled={dirtyCount === 0 || upsert.isPending}
            onClick={handleSave}
          >
            {upsert.isPending ? 'Saving…' : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
          </Button>
        </div>
      </div>

      {savedCount !== null && (
        <p className="text-sm text-sage-deep" data-testid="i18n-editor-saved">
          Saved {savedCount} translation{savedCount === 1 ? '' : 's'}.
        </p>
      )}
      {saveError && (
        <p className="text-sm text-honey-deep" data-testid="i18n-editor-save-error">
          {saveError}
        </p>
      )}
      {importError && (
        <p className="text-sm text-honey-deep" data-testid="i18n-editor-import-error">
          {importError}
        </p>
      )}

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : isError ? (
          <div className="p-6 text-ink-soft">
            Failed to load translations{error instanceof Error ? `: ${error.message}` : '.'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-ink-faint" data-testid="i18n-editor-empty">
            No keys match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3 w-1/4">Key / Context</th>
                <th className="px-4 py-3 w-1/4">Default (en-GB)</th>
                <th className="px-4 py-3">Translation</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.key}
                  className="border-b last:border-b-0 align-top"
                  style={{ borderColor: 'var(--color-hairline)' }}
                  data-testid={`i18n-editor-row-${row.key}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-ink break-all">{row.key}</div>
                    <div className="text-xs text-ink-faint mt-1">{row.context}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{row.defaultMessage}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={valueFor(row)}
                      placeholder="Untranslated"
                      data-testid={`i18n-editor-value-${row.key}`}
                      onChange={(e) =>
                        setEdits((prev) => ({ ...prev, [row.key]: e.target.value }))
                      }
                      className="w-full rounded-lg bg-paper px-3 py-2 text-sm text-ink border outline-none focus:border-honey focus:ring-2 focus:ring-honey-pale"
                      style={{ borderColor: 'var(--color-hairline)' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
